import { AnimatedNumber } from "@/components/portfolio/AnimatedNumber";
import { CardTitle } from "@/components/ui/Card";
import { HomeModernIcon } from "@heroicons/react/24/outline";

import {
  MetricButton,
  SectionBadge,
} from "@/components/hlidaci-pes/chart-line-interactive/Shared";
import { useLanguage } from "@/context/LanguageContext";
import { StatusPill } from "@/components/portfolio/StatusPill";
import { Panel, PanelGrid } from "@/components/ui/Panel";
import { EstateOffer } from "@/types";
import React from "react";

function parseNumericField(
  value: string | number | null | undefined,
): number | undefined {
  const parsed =
    typeof value === "string" ? parseFloat(value.replace(/[^\d.]/g, "")) : value;
  return parsed !== undefined && parsed !== null && !isNaN(parsed)
    ? parsed
    : undefined;
}

type ChartLineOverviewPanelsProps = {
  activeChart: string;
  total: {
    avgPrice: number;
    median: number;
    totalEstates: number;
    avgPricePerSquareMeter?: number;
    avgSquareMeter?: number;
  };
  analysis: {
    avgPriceBefore: number;
    avgPriceAfter: number;
    highestRent: number;
    avgSquareMeterBefore?: number;
    avgSquareMeterAfter?: number;
  };
  saleOffers: EstateOffer[];
  avgPriceLabel: string;
  medianLabel: string;
  totalEstatesLabel: string;
  avgPriceBeforeLabel: string;
  avgPriceAfterLabel: string;
  onSelectAvgPrice: () => void;
  onSelectAvgPriceBefore: () => void;
  onSelectAvgPriceAfter: () => void;
};

export function ChartLineOverviewPanels({
  activeChart,
  total,
  analysis,
  saleOffers,
  avgPriceLabel,
  medianLabel,
  totalEstatesLabel,
  avgPriceBeforeLabel,
  avgPriceAfterLabel,
  onSelectAvgPrice,
  onSelectAvgPriceBefore,
  onSelectAvgPriceAfter,
}: ChartLineOverviewPanelsProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      currency: "Kč",
      checking: "Zjišťujeme",
      trackedMetrics: "Sledované metriky",
      marketOverview: "Přehled trhu",
      metricDescription:
        "Vyberte metriku, kterou chcete promítnout do grafu a porovnat s aktuálním počtem nabídek.",
      overview: "Přehled",
      offersInCalculation: "Počet nabídek ve výpočtu.",
      avgPricePerSqm: "Průměrná cena za m²",
      avgSize: "Průměrná velikost",
      rentEstimate: "Odhad tržního nájmu",
      rentEstimateText: "Za kolik můžete pronajmout tuto nemovitost",
      rentEstimateDescription:
        "Kliknutím přepnete odhad do grafu a rychle porovnáte obě varianty.",
    },
    en: {
      currency: "CZK",
      checking: "Checking",
      trackedMetrics: "Tracked metrics",
      marketOverview: "Market overview",
      metricDescription:
        "Choose the metric to show in the chart and compare it with the current number of offers.",
      overview: "Overview",
      offersInCalculation: "Number of offers in the calculation.",
      avgPricePerSqm: "Avg price per m²",
      avgSize: "Avg size",
      rentEstimate: "Market rent estimate",
      rentEstimateText: "How much you can rent this property for",
      rentEstimateDescription:
        "Click to show the estimate in the chart and quickly compare both variants.",
    },
  }[language];

  // Price/m² computed from rent listings isn't meaningful, so for the
  // before/after reko tabs we derive it from the dedicated sale child
  // (offerType "Prodej") instead, calculated here from its raw offers.
  const saleAvgPricePerSquareMeter = React.useMemo(() => {
    const offersWithPricePerSqm = saleOffers.filter((offer) => {
      const pricePerSqm = parseNumericField(offer.pricePerSquareMeter);
      return pricePerSqm !== undefined && pricePerSqm > 0;
    });

    if (offersWithPricePerSqm.length === 0) {
      return undefined;
    }

    const totalPricePerSqm = offersWithPricePerSqm.reduce(
      (acc, offer) => acc + (parseNumericField(offer.pricePerSquareMeter) || 0),
      0,
    );
    return Math.round(totalPricePerSqm / offersWithPricePerSqm.length);
  }, [saleOffers]);

  // Determine which metrics to show based on active chart
  let avgPricePerSquareMeter: number | undefined;
  let avgSquareMeter: number | undefined;

  if (activeChart === "avgPriceBefore") {
    avgPricePerSquareMeter = total.avgPricePerSquareMeter;
    avgSquareMeter = total.avgSquareMeter;
  } else if (activeChart === "avgPriceAfter") {
    avgPricePerSquareMeter = saleAvgPricePerSquareMeter;
    avgSquareMeter = analysis.avgSquareMeterAfter;
  } else {
    avgPricePerSquareMeter = total.avgPricePerSquareMeter;
    avgSquareMeter = total.avgSquareMeter;
  }

  // Show panels if any metric has data
  const shouldShowMetrics =
    avgPricePerSquareMeter !== undefined ||
    avgSquareMeter !== undefined ||
    total.avgPricePerSquareMeter !== undefined ||
    total.avgSquareMeter !== undefined;

  const estimateBefore =
    analysis.avgPriceBefore > 0 ? (
      <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
        <AnimatedNumber value={analysis.avgPriceBefore} />
        <span className="text-sm font-medium text-foreground">
          {copy.currency}
        </span>
      </span>
    ) : (
      <span className="text-sm font-medium text-foreground">
        {copy.checking}
      </span>
    );

  const estimateAfter =
    analysis.avgPriceAfter > 0 ? (
      <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
        <AnimatedNumber value={analysis.avgPriceAfter} />
        <span className="text-sm font-medium text-foreground">-</span>
        <AnimatedNumber value={analysis.highestRent} />
        <span className="text-sm font-medium text-foreground">
          {copy.currency}
        </span>
      </span>
    ) : (
      <span className="text-sm font-medium text-foreground">
        {copy.checking}
      </span>
    );

  return (
    <PanelGrid className="xl:grid-cols-8 xl:grid-rows-3">
      <Panel className="xl:col-span-5 xl:row-span-3">
        <SectionBadge type="light">{copy.trackedMetrics}</SectionBadge>
        <CardTitle className="app-panel-title">{copy.marketOverview}</CardTitle>
        <p className="app-card-description">{copy.metricDescription}</p>
        <div className="mt-3 grid gap-1.5 md:grid-cols-6">
          <MetricButton
            label={avgPriceLabel}
            value={
              <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
                <AnimatedNumber value={total.avgPrice} />
                <span className="text-sm font-medium text-foreground ">
                  {copy.currency}
                </span>
              </span>
            }
            isActive={activeChart === "avgPrice"}
            onClick={onSelectAvgPrice}
            secondaryLabel={medianLabel}
            secondaryValue={`${total.median.toLocaleString(language === "cs" ? "cs-CZ" : "en-US")} ${copy.currency}`}
            className="md:col-span-4"
          />

          <Panel
            variant="compact"
            className="flex min-h-[5.25rem] flex-col justify-between md:col-span-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex size-7 items-center justify-center rounded-lg border border-white/70 bg-softiq-secondary-shade-100 text-softiq-primary shadow-sm">
                <HomeModernIcon className="h-3.5 w-3.5" />
              </div>
              <StatusPill>{copy.overview}</StatusPill>
            </div>

            <div className="space-y-1.5">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground ">
                {totalEstatesLabel}
              </span>
              <div className="app-metric-value">
                <AnimatedNumber value={total.totalEstates} />
              </div>
              <p className="text-[11px] leading-4 text-foreground ">
                {copy.offersInCalculation}
              </p>
            </div>
          </Panel>

          {shouldShowMetrics && (
            <>
              <Panel
                variant="compact"
                className="flex min-h-[5.25rem] flex-col justify-between md:col-span-3"
              >
                <div className="space-y-1.5">
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground">
                    {copy.avgPricePerSqm}
                  </span>
                  <div className="app-metric-value">
                    {avgPricePerSquareMeter !== undefined ? (
                      <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
                        <AnimatedNumber value={avgPricePerSquareMeter} />
                        <span className="text-sm font-medium text-foreground">
                          {copy.currency}/m²
                        </span>
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-foreground">
                        -
                      </span>
                    )}
                  </div>
                </div>
              </Panel>

              <Panel
                variant="compact"
                className="flex min-h-[5.25rem] flex-col justify-between md:col-span-3"
              >
                <div className="space-y-1.5">
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground">
                    {copy.avgSize}
                  </span>
                  <div className="app-metric-value">
                    {avgSquareMeter !== undefined ? (
                      <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
                        <AnimatedNumber value={avgSquareMeter} />
                        <span className="text-sm font-medium text-foreground">
                          m²
                        </span>
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-foreground">
                        -
                      </span>
                    )}
                  </div>
                </div>
              </Panel>
            </>
          )}
        </div>
      </Panel>

      <Panel className="flex flex-col xl:col-span-3 xl:row-span-3">
        <div className="space-y-1">
          <SectionBadge>{copy.rentEstimate}</SectionBadge>
          <p className="app-card-title">{copy.rentEstimateText}</p>
          <p className="app-card-description">{copy.rentEstimateDescription}</p>
        </div>

        <div className="mt-3 flex flex-1 flex-col justify-center gap-3">
          <MetricButton
            label={avgPriceBeforeLabel}
            value={estimateBefore}
            isActive={activeChart === "avgPriceBefore"}
            onClick={onSelectAvgPriceBefore}
          />

          <MetricButton
            label={avgPriceAfterLabel}
            value={estimateAfter}
            isActive={activeChart === "avgPriceAfter"}
            onClick={onSelectAvgPriceAfter}
          />
        </div>
      </Panel>
    </PanelGrid>
  );
}
